import { useEffect, useMemo } from 'react'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { batch, useSelector } from 'react-redux'
import { useAppDispatch } from 'state'
import { useFastRefreshEffect, useSlowRefreshEffect } from 'hooks/useRefreshEffect'
import { FAST_INTERVAL } from 'config/constants'
import useSWRImmutable from 'swr/immutable'
import { getFarmConfig } from '@pancakeswap/farms/constants'
import { livePools } from 'config/constants/pools'
import { Pool } from '@pancakeswap/uikit'
import { Token } from '@pancakeswap/sdk'

import { useActiveChainId } from 'hooks/useActiveChainId'
import {
  fetchPoolsPublicDataAsync,
  fetchPoolsUserDataAsync,
  fetchCakeVaultPublicData,
  fetchCakeVaultUserData,
  fetchCakePoolUserDataAsync,
  fetchCakePoolPublicDataAsync,
  fetchVaultPublicData,
  fetchVaultUserData,
  fetchVaultFeeDatas,
} from '.'
import { VaultKey } from '../types'
import { fetchFarmsPublicDataAsync } from '../farms'
import {
  makePoolWithUserDataLoadingSelector,
  makeVaultPoolByKey,
  poolsWithVaultSelector,
  ifoCreditSelector,
  ifoCeilingSelector,
  makeVaultPoolWithKeySelector,
} from './selectors'

const lPoolAddresses = livePools.filter(({ sousId }) => sousId !== 0).map(({ earningToken }) => earningToken.address)

// Only fetch farms for live pools
const getActiveFarms = async (chainId: number) => {
  const farmsConfig = await getFarmConfig(chainId)
  return farmsConfig
    .filter(
      ({ token, pid, quoteToken }) =>
        pid !== 0 &&
        ((token.symbol === 'BBC' && quoteToken.symbol === 'WETH') ||
          (token.symbol === 'USDC' && quoteToken.symbol === 'WETH') ||
          (token.symbol === 'USDT' && quoteToken.symbol === 'BUSD') ||
          lPoolAddresses.find((poolAddress) => poolAddress === token.address)),
    )
    .map((farm) => farm.pid)
}

const getCakePriceFarms = async (chainId: number) => {
  const farmsConfig = await getFarmConfig(chainId)
  return farmsConfig
    .filter(
      ({ token, pid, quoteToken }) =>
        pid !== 0 &&
        ((token.symbol === 'BBC' && quoteToken.symbol === 'WETH') ||
          (token.symbol === 'USDC' && quoteToken.symbol === 'WETH')),
    )
    .map((farm) => farm.pid)
}

export const useFetchPublicPoolsData = () => {
  const dispatch = useAppDispatch()
  const { chainId } = useActiveChainId()

  useSlowRefreshEffect(
    (currentBlock) => {
      const fetchPoolsDataWithFarms = async () => {
        const activeFarms = await getActiveFarms(chainId)
        await dispatch(fetchFarmsPublicDataAsync({ pids: activeFarms, chainId }))
        
        batch(() => {
          dispatch(fetchPoolsPublicDataAsync(currentBlock, chainId))
        })
      }

      fetchPoolsDataWithFarms()
    },
    [dispatch, chainId],
  )
}

export const usePool = (sousId: number): { pool: Pool.DeserializedPool<Token>; userDataLoaded: boolean } => {
  const poolWithUserDataLoadingSelector = useMemo(() => makePoolWithUserDataLoadingSelector(sousId), [sousId])
  return useSelector(poolWithUserDataLoadingSelector)
}

export const usePoolsWithVault = () => {
  return useSelector(poolsWithVaultSelector)
}

export const useDeserializedPoolByVaultKey = (vaultKey) => {
  const vaultPoolWithKeySelector = useMemo(() => makeVaultPoolWithKeySelector(vaultKey), [vaultKey])

  return useSelector(vaultPoolWithKeySelector)
}

export const usePoolsPageFetch = () => {
  // const { address: account } = useAccount()
  const { account, chainId } = useActiveWeb3React()
  const dispatch = useAppDispatch()
  useFetchPublicPoolsData()

  useFastRefreshEffect(() => {
    batch(() => {
      dispatch(fetchVaultPublicData({ chainId }))
      if (account) {
        dispatch(fetchVaultUserData({ account, chainId }))
        dispatch(fetchPoolsUserDataAsync({ account, chainId }))
      }
    })
  }, [account, chainId, dispatch])

  useEffect(() => {
    dispatch(fetchVaultFeeDatas({ chainId }))
    // batch(() => {
      // dispatch(fetchCakeFlexibleSideVaultFees())
      // livePools.filter(pool => !!pool.vaultKey).map(pool => {
      //   if(IsLocked(pool.vaultKey))
      //     dispatch(fetchLockedVaultFees({ vaultKey: pool.vaultKey, chainId, contractAddress: pool.contractAddress[chainId] }))
      //   else
      //     dispatch(fetchFlexibleVaultFees({ vaultKey: pool.vaultKey, chainId, contractAddress: pool.contractAddress[chainId] }))
      //   return undefined
      // })
    // })
  }, [chainId, dispatch])
}

export const useCakeVaultUserData = () => {
  const { account, chainId } = useActiveWeb3React()
  const dispatch = useAppDispatch()

  useFastRefreshEffect(() => {
    if (account) {
      dispatch(fetchCakeVaultUserData({ account, chainId }))
    }
  }, [account, chainId, dispatch])
}

export const useCakeVaultPublicData = () => {
  const { chainId } = useActiveWeb3React()
  const dispatch = useAppDispatch()
  useFastRefreshEffect(() => {
    dispatch(fetchCakeVaultPublicData({chainId}))
  }, [dispatch, chainId])
}

export const useFetchIfo = () => {
  const { account, chainId } = useActiveWeb3React()
  const dispatch = useAppDispatch()

  useSWRImmutable(
    'fetchIfoPublicData',
    async () => {
      const cakePriceFarms = await getCakePriceFarms(chainId)
      await dispatch(fetchFarmsPublicDataAsync({ pids: cakePriceFarms, chainId }))
      batch(() => {
        dispatch(fetchCakePoolPublicDataAsync(chainId))
        dispatch(fetchCakeVaultPublicData({chainId}))
        // dispatch(fetchIfoPublicDataAsync())
      })
    },
    {
      refreshInterval: FAST_INTERVAL,
    },
  )

  useSWRImmutable(
    account && ['fetchIfoUserData', account],
    async () => {
      batch(() => {
        dispatch(fetchCakePoolUserDataAsync(account, chainId))
        dispatch(fetchCakeVaultUserData({ account, chainId }))
        // dispatch(fetchUserIfoCreditDataAsync(account))
      })
    },
    {
      refreshInterval: FAST_INTERVAL,
    },
  )

  useSWRImmutable('fetchCakeVaultFees', async () => {
    dispatch(fetchVaultFeeDatas({chainId}))
  })
}

export const useCakeVault = () => {
  return useVaultPoolByKey(VaultKey.CakeVault)
}

export const useVaultPoolByKey = (key: VaultKey) => {
  const vaultPoolByKey = useMemo(() => makeVaultPoolByKey(key), [key])

  return useSelector(vaultPoolByKey)
}

export const useIfoCredit = () => {
  return useSelector(ifoCreditSelector)
}

export const useIfoCeiling = () => {
  return useSelector(ifoCeilingSelector)
}
